import React, { useEffect, useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  CarFront,
  Cog,
  Copy,
  FileJson,
  Info,
  ListCheck,
  Loader,
  Loader2,
  ShieldCheck,
} from "lucide-react";

import { isAlphanumeric } from "validator";
import { useService } from "../../../../context/ServiceContext";
import { showErrorToast, showSuccessToast } from "../../../../utils/toastUtils";
import { fetchProfile } from "../../../../Services/profile/profileservice";
import Seo from "../../../../Components/SeoComponent/Seo";
import Header from "../../../../Components/Header";
import { getRcProUnmasked } from "../../../../Services/ApiTestService/apiTestService";

function RcProUnmasked() {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const [vehicleNo, setVehicleNo] = useState("");
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);
  const { serviceName } = useService();

  //  "chasisNum": "MD637GE53R2A06894"

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const isAlpha = (value) => {
    if (!value) return false;

    return isAlphanumeric(String(value).trim(), "en-IN", { ignore: " " });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("Copied!"));

    setIsJsonCopied(true);
  };

  const handleRcProUnMasked = async () => {
    if (!profileData) return;

    const validateVehicleNum = isAlpha(vehicleNo);

    if ( !vehicleNo) {
      return setInputError("Vehicle Number is Required");
    }

    if (vehicleNo && !validateVehicleNum) {
      return setInputError("⚠️ Please enter alpha numeric in Vehicle No. field");
    }
    
    try {
      setInputError(null);
      setIsLoading(true);
      setResponse(null);

      const res = await getRcProUnmasked({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        vehicle_number: vehicleNo.trim()?.toUpperCase(),
      });

      if (res) {
        apiResponse = res;
      }
      setResponse(apiResponse);

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else if (res?.status?.code === 422) {
        showErrorToast(res?.data?.message || "invalid details ");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!res.success) {
        showErrorToast(res?.data?.message || "verificaton failed");
      } else {
        showErrorToast(res?.data?.message || "something went wrong");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading={serviceName} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center justify-start pb-14">
        {/* ---------------- FORM ---------------- */}
        <div className="max-w-4xl w-full mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-100">
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-slate-100 pb-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
              <CarFront size={24} />
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-800">
               {serviceName}
              </h2>
              <p className="text-sm text-slate-500">
                Verify vehicle details using the engine and chassis number.
              </p>
            </div>
          </div>

          {/* Inputs */}
          <div className="mt-6  gap-6 flex justify-center items-center">
    
            {/* Vehicle Number */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Cog size={17} className="text-blue-600" />
                Vehicle Number
              </label>

              <input
                type="text"
                value={vehicleNo}
                onChange={(e) => setVehicleNo(e.target.value)}
                placeholder="Eg: GJ0***1256"
                className="w-full rounded-xl border uppercase border-slate-300 px-4 py-3 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </div>

                {/* Button */}
          <div className="mt-8 flex ">
            <button
              disabled={isLoading || (!vehicleNo)}
              onClick={handleRcProUnMasked}
              className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-7 py-3 font-semibold text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  Verify Vehicle
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </>
              )}
            </button>
          </div>
          </div>

          {/* Error */}
          {inputError && (
            <div className="mt-5 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm font-medium">{inputError}</span>
            </div>
          )}

      
        </div>

        {/* response message */}

        {response && (
          <div
            className={` ${response.status?.code === 500 || response.status?.code === 422 ? "text-red-500" : "text-green-600"} py-4 text-2xl font-bold`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* instructions */}

        <div className="w-full flex justify-center">
          <div className="bg-green-100 max-w-3xl border border-green-400 text-green-700 p-5 mt-12 rounded-2xl">
            <h3 className="font-bold flex items-center justify-start gap-2 text-lg mb-2">
              <Info /> Important Instructions{" "}
              <span className="text-red-600">*</span>
            </h3>

            <p className="text-sm leading-relaxed">
              Please enter a valid Vehicle Number. Clicking the Verify button.
              After successful verification, the system will return the response
              in structured format, including contact details associated with
              the provided details. You may review and copy the data for your
              reference or further use.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8"
              >
                {isJsonCopied ? "copied" : <Copy />}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-2" />{" "}
              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            <div className="bg-gray-200 p-2 rounded-md">
              <h2 className="text-2xl flex items-center justify-center gap-2 font-bold text-blue-500 mb-1">
                <ListCheck />
                RC Data
              </h2>

              {response?.status?.code === 200 && response?.data && (
  <div className="space-y-6">

    {/* =========================================================
        RC HEADER
    ========================================================= */}
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">

      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm text-gray-500">
            Registration Number
          </p>

          <h2 className="text-2xl font-bold text-gray-900">
            {response?.data?.regNo || "-"}
          </h2>
        </div>

        <span
          className={`px-4 py-1.5 rounded-full text-sm font-semibold ${
            response?.data?.status === "ACTIVE"
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {response?.data?.status || "-"}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 p-6">

        <InfoItem
          label="Vehicle"
          value={`${response?.data?.vehicleManufacturerName || ""} ${
            response?.data?.model || ""
          }`}
        />

        <InfoItem
          label="Vehicle Class"
          value={response?.data?.vehicleClass}
        />

        <InfoItem
          label="Fuel Type"
          value={response?.data?.type}
        />

        <InfoItem
          label="Colour"
          value={response?.data?.vehicleColour}
        />

        <InfoItem
          label="Registration Authority"
          value={response?.data?.regAuthority}
        />

        <InfoItem
          label="Registration Date"
          value={response?.data?.regDate}
        />
      </div>

    </div>


    {/* =========================================================
        OWNER DETAILS
    ========================================================= */}
    <SectionCard title="Owner Details">

      <InfoGrid>

        <InfoItem
          label="Owner Name"
          value={response?.data?.owner}
        />

        <InfoItem
          label="Father's Name"
          value={response?.data?.ownerFatherName}
        />

        <InfoItem
          label="Ownership Number"
          value={response?.data?.ownerCount}
        />

        <InfoItem
          label="Present Address"
          value={response?.data?.presentAddress}
        />

        <InfoItem
          label="Permanent Address"
          value={response?.data?.permanentAddress}
        />

      </InfoGrid>

    </SectionCard>


    {/* =========================================================
        REGISTRATION DETAILS
    ========================================================= */}
    <SectionCard title="Registration Details">

      <InfoGrid>

        <InfoItem
          label="Registration Number"
          value={response?.data?.regNo}
        />

        <InfoItem
          label="State Code"
          value={response?.data?.stateCode}
        />

        <InfoItem
          label="Registration Date"
          value={response?.data?.regDate}
        />

        <InfoItem
          label="RC Expiry Date"
          value={response?.data?.rcExpiryDate}
        />

        <InfoItem
          label="Status As On"
          value={response?.data?.statusAsOn}
        />

        <InfoItem
          label="Tax Valid Upto"
          value={response?.data?.vehicleTaxUpto}
        />

        <InfoItem
          label="Manufacturing Month / Year"
          value={response?.data?.vehicleManufacturingMonthYear}
        />

        <InfoItem
          label="Vehicle Category"
          value={response?.data?.vehicleCategory}
        />

      </InfoGrid>

    </SectionCard>


    {/* =========================================================
        VEHICLE DETAILS
    ========================================================= */}
    <SectionCard title="Vehicle Details">

      <InfoGrid>

        <InfoItem
          label="Manufacturer"
          value={response?.data?.vehicleManufacturerName}
        />

        <InfoItem
          label="Model"
          value={response?.data?.model}
        />

        <InfoItem
          label="Variant"
          value={response?.data?.variantName}
        />

        <InfoItem
          label="Vehicle Class"
          value={response?.data?.vehicleClass}
        />

        <InfoItem
          label="Body Type"
          value={response?.data?.bodyType}
        />

        <InfoItem
          label="Fuel Type"
          value={response?.data?.type}
        />

        <InfoItem
          label="Emission Norm"
          value={response?.data?.normsType}
        />

        <InfoItem
          label="Vehicle Colour"
          value={response?.data?.vehicleColour}
        />

        <InfoItem
          label="Chassis Number"
          value={response?.data?.chassis}
        />

        <InfoItem
          label="Engine Number"
          value={response?.data?.engine}
        />

      </InfoGrid>

    </SectionCard>


    {/* =========================================================
        TECHNICAL DETAILS
    ========================================================= */}
    <SectionCard title="Technical Specifications">

      <InfoGrid>

        <InfoItem
          label="Engine Capacity"
          value={
            response?.data?.vehicleCubicCapacity
              ? `${response.data.vehicleCubicCapacity} CC`
              : null
          }
        />

        <InfoItem
          label="Gross Vehicle Weight"
          value={
            response?.data?.grossVehicleWeight
              ? `${response.data.grossVehicleWeight} kg`
              : null
          }
        />

        <InfoItem
          label="Unladen Weight"
          value={
            response?.data?.unladenWeight
              ? `${response.data.unladenWeight} kg`
              : null
          }
        />

        <InfoItem
          label="Number of Cylinders"
          value={response?.data?.vehicleCylindersNo}
        />

        <InfoItem
          label="Seating Capacity"
          value={response?.data?.vehicleSeatCapacity}
        />

        <InfoItem
          label="Sleeper Capacity"
          value={response?.data?.vehicleSleeperCapacity}
        />

        <InfoItem
          label="Standing Capacity"
          value={response?.data?.vehicleStandingCapacity}
        />

        <InfoItem
          label="Wheelbase"
          value={
            response?.data?.wheelbase
              ? `${response.data.wheelbase} mm`
              : null
          }
        />

      </InfoGrid>

    </SectionCard>


    {/* =========================================================
        INSURANCE
    ========================================================= */}
    <SectionCard title="Insurance Details">

      <InfoGrid>

        <InfoItem
          label="Insurance Company"
          value={response?.data?.vehicleInsuranceCompanyName}
        />

        <InfoItem
          label="Policy Number"
          value={response?.data?.vehicleInsurancePolicyNumber}
        />

        <InfoItem
          label="Insurance Valid Upto"
          value={response?.data?.vehicleInsuranceUpto}
        />

      </InfoGrid>

    </SectionCard>


    {/* =========================================================
        FINANCE
    ========================================================= */}
    <SectionCard title="Finance Details">

      <InfoGrid>

        <InfoItem
          label="Financer"
          value={response?.data?.rcFinancer}
        />

        <InfoItem
          label="Financed"
          value={response?.data?.financed ? "Yes" : "No"}
        />

      </InfoGrid>

    </SectionCard>


    {/* =========================================================
        PUCC
    ========================================================= */}
    <SectionCard title="PUCC Details">

      <InfoGrid>

        <InfoItem
          label="PUCC Number"
          value={response?.data?.puccNumber}
        />

        <InfoItem
          label="PUCC Valid Upto"
          value={response?.data?.puccUpto}
        />

      </InfoGrid>

    </SectionCard>


    {/* =========================================================
        PERMIT
    ========================================================= */}
    <SectionCard title="Permit Details">

      <InfoGrid>

        <InfoItem
          label="Permit Number"
          value={response?.data?.permitNumber}
        />

        <InfoItem
          label="Permit Type"
          value={response?.data?.permitType}
        />

        <InfoItem
          label="Permit Issue Date"
          value={response?.data?.permitIssueDate}
        />

        <InfoItem
          label="Permit Valid From"
          value={response?.data?.permitValidFrom}
        />

        <InfoItem
          label="Permit Valid Upto"
          value={response?.data?.permitValidUpto}
        />

        <InfoItem
          label="National Permit Number"
          value={response?.data?.nationalPermitNumber}
        />

        <InfoItem
          label="National Permit Valid Upto"
          value={response?.data?.nationalPermitUpto}
        />

        <InfoItem
          label="National Permit Issued By"
          value={response?.data?.nationalPermitIssuedBy}
        />

      </InfoGrid>

    </SectionCard>


    {/* =========================================================
        OTHER DETAILS
    ========================================================= */}
    <SectionCard title="Other Information">

      <InfoGrid>

        <InfoItem
          label="Commercial Vehicle"
          value={response?.data?.isCommercial ? "Yes" : "No"}
        />

        <InfoItem
          label="Blacklist Status"
          value={response?.data?.blacklistStatus || "Not Blacklisted"}
        />

        <InfoItem
          label="Non Use Status"
          value={response?.data?.nonUseStatus}
        />

        <InfoItem
          label="Non Use From"
          value={response?.data?.nonUseFrom}
        />

        <InfoItem
          label="Non Use To"
          value={response?.data?.nonUseTo}
        />

        <InfoItem
          label="NOC Details"
          value={response?.data?.nocDetails}
        />

      </InfoGrid>

    </SectionCard>

  </div>
)}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default RcProUnmasked;


const SectionCard = ({ title, children }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      
      <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="text-base font-semibold text-gray-800">
          {title}
        </h3>
      </div>

      <div className="p-5">
        {children}
      </div>

    </div>
  );
};


const InfoGrid = ({ children }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
      {children}
    </div>
  );
};


const InfoItem = ({ label, value }) => {
  const displayValue =
    value !== null &&
    value !== undefined &&
    value !== ""
      ? value
      : "-";

  return (
    <div className="min-w-0">

      <p className="text-xs font-medium text-gray-500 mb-1">
        {label}
      </p>

      <p className="text-sm font-semibold text-gray-900 break-words">
        {displayValue}
      </p>

    </div>
  );
};